import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Text = styled.div `
  transition: transform 0.3s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ButtonInner = styled.div `
  padding: 14px 4px;
  transition: color 0.3s;
  height: var(--buttonHeight);
  display: flex;
  column-gap: 10px;
  flex-wrap: nowrap;
  align-items: center;
`;
const LeftEnd = styled.div `
  position: relative;
  height: var(--buttonHeight);
  width: 21px;
  margin-right: -2px;
`;
const RightEnd = styled.div `
  position: relative;
  height: var(--buttonHeight);
  width: 20px;
  transform: scale(-1);
  margin-left: -2px;
`;
const Border = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  svg {
    height: var(--buttonHeight);
    color: var(--brandColour);
  }
`;
const Fill = styled.div `
  position: absolute;
  top: 0;
  right: 0;

  @supports (-webkit-touch-callout: none) {
    /* CSS specific to iOS devices */
    right: -1px;
  }

  svg {
    height: var(--buttonHeight);
  }
`;
const SolidStyles = css `
  ${ButtonInner} {
    border: none;
    border-top: 2.5px solid var(--brandColour);
    border-bottom: 2.5px solid var(--brandColour);
    background-color: var(--brandColour);
    color: var(--textColour);
  }

  ${Fill} {
    color: var(--brandColour);
  }

  @media (hover: hover) {
    &:hover {
      ${ButtonInner} {
        border-color: var(--brandColour);
        background-color: transparent;
        color: var(--hoverTextColour);
      }

      ${Fill} {
        opacity: 0;
      }
    }
  }

  /* &:active {
    ${ButtonInner} {
      border-color: var(--brandColour);
      background-color: var(--activeBgColour);
      color: var(--hoverTextColour);
    }

    ${Fill} {
      opacity: 0.2;
    }
  } */
`;
const OutlineStyles = css `
  ${ButtonInner} {
    background-color: transparent;
    color: var(--hoverTextColour);
    border: none;
    border-top: var(--borderThickness) solid var(--brandColour);
    border-bottom: var(--borderThickness) solid var(--brandColour);
  }

  ${Fill} {
    opacity: 0;
    color: var(--brandColour);
  }

  @media (hover: hover) {
    &:hover {
      ${ButtonInner} {
        border-color: var(--brandColour);
        background-color: var(--brandColour);
        color: var(--textColour);
      }

      ${Fill} {
        opacity: 1;
      }
    }
  }

  /* &:active {
    ${ButtonInner} {
      border-color: var(--brandColour);
      background-color: var(--activeOutlineBgColour);
      color: var(--textColour);
    }

    ${Fill} {
      opacity: 0.7;
    }
  } */

  @supports (-webkit-touch-callout: none) {
    /* CSS specific to iOS devices */
    ${LeftEnd} {
      transform: skewY(-1deg);
      right: -1px;
    }

    /* Button line fix for Safari MacOS */
    _:default:not(:root:root),
    ${LeftEnd} {
      transform: skewY(-1deg);
      right: -1px;
    }
  }
`;
const hideText = css `
  ${Text} {
    @media ${until(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const Icon = styled.div `
  display: inline-block;
  vertical-align: middle;
  padding-top: 2px;
  transition: transform 0.3s;
  line-height: 0;

  svg {
    max-height: 22px;
    max-width: 22px;
    width: 100%;
  }
`;
const LargeStyles = css `
  --buttonHeight: 65px;

  @media ${from(Device.TabletLarge)} {
    --borderThickness: 3.2px;
  }

  ${ButtonInner} {
    padding: 16px 4px;
    /* border-width: 3px; */
  }

  ${LeftEnd},
  ${RightEnd} {
    width: 28px;
  }

  ${Text} {
    font-size: ${fluidAdjusted(18, 28)};
    line-height: ${fluidAdjusted(26, 36)};
  }

  ${Icon} {
    padding-top: 1px;
  }
`;
const SmallStyles = css `
  --buttonHeight: 45px;

  ${ButtonInner} {
    padding: 16px 4px;
    border-width: 2px;
  }

  ${LeftEnd},
  ${RightEnd} {
    width: 24px;
  }

  ${Text} {
    font-size: ${fluidAdjusted(14, 14)};
    line-height: ${fluidAdjusted(22, 22)};
  }

  ${Icon} {
    padding-top: 0;

    svg {
      max-height: 18px;
      max-width: 18px;
    }
  }
`;
const Button = styled.button `
  --brandColour: ${brand.secondary.red5};
  --textColour: ${brand.black};
  --hoverTextColour: ${brand.white};
  --activeBgColour: ${rgba(brand.secondary.red5, 0.2)};
  --activeOutlineBgColour: ${rgba(brand.secondary.red5, 0.7)};
  --borderThickness: 2.5px;
  --buttonHeight: 58px;

  display: inline-flex;
  align-items: center;
  border: none;
  padding: 0;
  background: transparent;
  user-select: none;
  text-decoration: none;

  ${Text} {
    font-family: var(--fontFamily);
    font-weight: 900;
    font-size: ${fluidAdjusted(16, 20)};
    line-height: ${fluidAdjusted(21, 26)};
    margin-top: 0;
    display: inline-block;
    vertical-align: middle;
  }

  &[data-branding='red'] {
    --brandColour: ${brand.secondary.red5};
    --textColour: ${brand.white};
    --hoverTextColour: ${brand.secondary.red5};
    --activeBgColour: ${rgba(brand.secondary.red5, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.secondary.red5, 0.7)};
  }

  &[data-branding='peach'] {
    --brandColour: ${brand.buff.buff4};
    --textColour: ${brand.secondary.red5};
    --hoverTextColour: ${brand.secondary.red5};
    --activeBgColour: ${rgba(brand.buff.buff4, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.buff.buff4, 0.7)};
  }

  &[data-branding='purple'] {
    --brandColour: ${brand.tertiary.pinkAlt};
    --textColour: ${brand.secondary.red5};
    --hoverTextColour: ${brand.secondary.red5};
    --activeBgColour: ${rgba(brand.tertiary.pinkAlt, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.tertiary.pinkAlt, 0.7)};
  }

  &[data-branding='white'] {
    --brandColour: ${brand.white};
    --textColour: ${brand.black};
    --hoverTextColour: ${brand.white};
    --activeBgColour: ${rgba(brand.white, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.white, 0.7)};
  }

  &[data-branding='pink'] {
    --brandColour: ${brand.tertiary.pinkAlt};
    --textColour: ${brand.black};
    --hoverTextColour: ${brand.black};
    --activeBgColour: ${rgba(brand.tertiary.pinkAlt, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.tertiary.pinkAlt, 0.7)};
  }

  &[data-branding='black'] {
    --brandColour: ${brand.black};
    --textColour: ${brand.white};
    --hoverTextColour: ${brand.black};
    --activeBgColour: ${rgba(brand.black, 0.2)};
    --activeOutlineBgColour: ${rgba(brand.black, 0.7)};
  }

  ${({ styleType }) => {
    switch (styleType) {
        case 'solid':
            return SolidStyles;
        case 'outline':
            return OutlineStyles;
        default:
            break;
    }
}}

  ${({ iconPosition }) => {
    switch (iconPosition) {
        case 'right':
            return hideText;
        default:
            break;
    }
}}

  ${({ size }) => {
    switch (size) {
        case 'small':
            return SmallStyles;
        case 'large':
            return LargeStyles;
        default:
            break;
    }
}}

  @media (hover: hover) {
    &:hover {
      ${Text} {
        transform: translateX(2px);
      }
      ${Icon} {
        transform: translateX(-2px);
      }
    }
  }

  &:focus {
    outline: none;
    outline-offset: 3px;
    outline-color: currentColor;
  }
`;
export const ButtonStyles = {
    Border,
    Button,
    ButtonInner,
    Fill,
    Icon,
    LeftEnd,
    RightEnd,
    Text,
};
